/* eslint-disable react-hooks/exhaustive-deps */
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Heading, Icons, TextInput } from '@sede-enterprise/shell-ds-react-framework';
import { useNavigate } from 'react-router';
import dayjs from 'dayjs';

import Table from '../../FieldNomination/Table';
import Card from '../../../../UI/Card';
import { Col, FormRow, Row } from '../../../../UI/Grid';
import Loader from '../../../../UI/Loader';
import { sendNotification } from '../../../../UI/Notification';
import {
  DOWNLOAD_ZKEY_MAPPINGS,
  GET_ZKEY_MAPPING,
  MESSAGES,
} from '../../../../constants/BeachSwaps';
import { GenesisApi, get } from '../../../../services/GenesisApi';
import { IZkeyMapping } from '../../../../types/BeachSwaps.type';
import searchByText from '../../../../utils/searchFunction';
import AddEditZKeyMapping from '../AddEditZKeyMapping';
import { BeachSwapWrapper, TableLoaderContainer } from '../style';
import AuditLogs from './AuditLogs';
import { IFullState } from '../../../../types/All.type';
import { rolePermissions } from '../../../../constants/Permission';

const { DownloadSolid, Edit, ArrowLeft, InfoCircle: IconCircleInfo } = Icons;

const CoastKeyMapper: React.FC = () => {
  const navigate = useNavigate();

  const { permissions } = useSelector((state: IFullState) => state.genesisUserRolePermission);

  const [open, setOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [downloading, setDownloading] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [data, setData] = useState<IZkeyMapping[]>([]);
  const [openAudit, setOpenAudit] = useState<boolean>(false);

  const [selectedItem, setSelectedItem] = useState<IZkeyMapping | null>(null);
  const [editType, setEditType] = useState<'add' | 'edit' | null>(null);

  const getZkeyMapping = () => {
    setLoading(true);
    get(GET_ZKEY_MAPPING)
      .then((response) => {
        setData(response?.data ?? []);
      })
      .catch(() => {
        sendNotification({
          type: 'error',
          message: MESSAGES.SERVER_ERROR,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getZkeyMapping();
  }, []);

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const renderData = (text: string, mappings: IZkeyMapping[]) => {
    return searchByText(mappings, text, [
      'deal_DealLegId',
      'npb_Deal_DealLegId',
      'dealUnit',
      'swapType',
      'counterPartyName',
      'terminal',
      'buySell',
      'primarySecondary',
      'subTerminalName',
      'validFrom',
      'validTo',
      'primarySecondary',
    ]);
  };

  const handleZKeyModal = (value: boolean) => {
    setOpen(value);
    if (!value) {
      setSelectedItem(null);
    }
  };

  const handleEditOnClick = (item: IZkeyMapping, type: 'add' | 'edit') => {
    setEditType(type);
    setSelectedItem(item);
    handleZKeyModal(true);
  };

  const handleAuditModal = (value: boolean) => {
    setOpenAudit(value);
    if (!value) {
      setSelectedItem(null);
    }
  };

  const handleAuditOnClick = (item: IZkeyMapping) => {
    setSelectedItem(item);
    handleAuditModal(true);
  };

  const handleCloseOnClick = () => {
    navigate(-1);
  };

  const handleDownloadOnClick = () => {
    setDownloading(true);
    GenesisApi({
      url: DOWNLOAD_ZKEY_MAPPINGS,
      method: 'POST',
      responseType: 'blob',
    })
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Mappings_${dayjs().format()}.xlsx`);
        document.body.appendChild(link);
        link.click();
      })
      .finally(() => {
        setDownloading(false);
      });
  };

  const renderActions = (item: IZkeyMapping) => (
    <div className='grid-actions'>
      <Button
        iconOnly
        icon={<Edit />}
        variant='outlined'
        size='xsmall'
        title='Edit'
        onClick={() => {
          handleEditOnClick(item, 'edit');
        }}
        disabled={!permissions.includes(rolePermissions.updateBeachSwaps)}
        data-testid={`edit-button-${item.id}`}
      />
      <Button
        iconOnly
        icon={<IconCircleInfo />}
        variant='outlined'
        size='xsmall'
        title='Audit Logs'
        onClick={() => {
          handleAuditOnClick(item);
        }}
        data-testid={`audit-button-${item.id}`}
      />
    </div>
  );

  const renderOnlyDate = (date: string) => {
    return date ? dayjs(date).format('DD MMM YYYY') : undefined;
  };

  const columns = [
    {
      accessorKey: 'deal_DealLegId',
      header: 'Deal ID',
      sorting: true,
    },
    {
      accessorKey: 'nbp_Deal_DealLegId',
      header: 'NBP Deal ID',
      sorting: true,
    },
    {
      accessorKey: 'dealUnit',
      header: 'Deal Unit',
      sorting: true,
    },
    {
      accessorKey: 'swapType',
      header: 'Swap Type',
      sorting: true,
    },
    {
      accessorKey: 'counterPartyName',
      header: 'Counterparty',
      sorting: true,
    },
    {
      accessorKey: 'terminalName',
      header: 'Terminal',
      sorting: true,
    },
    {
      accessorKey: 'subTerminalName',
      header: 'Sub Terminal',
      sorting: true,
    },
    {
      accessorKey: 'buySell',
      header: 'Buy/Sell',
      sorting: true,
    },
    {
      accessorKey: 'primarySecondary',
      header: 'Primary/ Secondary',
    },
    {
      accessorKey: 'validFrom',
      header: 'Valid From',
      sorting: true,
      cell: ({ row }) => renderOnlyDate(row?.original?.validFrom),
    },
    {
      accessorKey: 'validTo',
      header: 'Valid To',
      sorting: true,
      cell: ({ row }) => renderOnlyDate(row?.original?.validTo),
    },
    {
      accessorKey: '',
      header: 'Actions',
      cell: ({ row }) => renderActions(row?.original),
    },
  ];

  return (
    <BeachSwapWrapper>
      <Row>
        <Col size={12}>
          <Card>
            <FormRow>
              <Col size={12} className='header-with-close'>
                <Button
                  iconOnly
                  icon={<ArrowLeft />}
                  onClick={handleCloseOnClick}
                  variant='transparent'
                />
                <Heading type='h2'>Counterparty - zKey mapping</Heading>
              </Col>
            </FormRow>
            <FormRow className='filter-container'>
              <Col size={3} className='date-range'>
                <TextInput placeholder='Search' onChange={handleSearch} />
              </Col>
              <Col size={9} className='button-wrapper'>
                <Button
                  size='small'
                  onClick={() => {
                    setEditType('add');
                    handleZKeyModal(true);
                  }}
                  disabled={!permissions.includes(rolePermissions.createBeachSwaps)}
                >
                  Add New
                </Button>
                <Button
                  size='small'
                  icon={<DownloadSolid />}
                  onClick={handleDownloadOnClick}
                  loading={downloading}
                  disabled={
                    !permissions.includes(rolePermissions.downloadBeachSwaps) || downloading
                  }
                >
                  Download
                </Button>
              </Col>
            </FormRow>
            <FormRow className='table-container mapping-table'>
              {loading && (
                <TableLoaderContainer>
                  <div data-testid='loader' className='loader-wrapper'>
                    <Loader />
                  </div>
                </TableLoaderContainer>
              )}
              <Col size={12}>
                <Table columns={columns} data={renderData(searchText, data)} />
              </Col>
            </FormRow>
          </Card>
        </Col>
      </Row>
      {open && (
        <AddEditZKeyMapping
          open={open}
          selectedItem={selectedItem}
          onClose={() => handleZKeyModal(false)}
          refreshData={getZkeyMapping}
          editType={editType}
        />
      )}
      {openAudit && (
        <AuditLogs
          open={openAudit}
          selectedItem={selectedItem}
          onClose={() => handleAuditModal(false)}
        />
      )}
    </BeachSwapWrapper>
  );
};

export default CoastKeyMapper;
